import React, { useEffect, useState } from 'react';
import { CollectionType, ProductType } from '@/@types';
import { useUser } from '@/components/context/user';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { useToast } from '@/components/context/toast';
import { UIComponent, UIProp } from '../..';
import { Icon, Modal } from '../../atoms';
import { Input } from '../../form';
import * as styles from './add-to-collection.styles';
import CollectionLogo from '../../molecules/collection-logo';
import CollectionForm from '../collection-form';

const AddToCollection: UIComponent<
  UIProp<typeof Modal> & {
    productId?: ProductType['id'];
    /**
     * if set will call this callback instead of saving the product on the collection
     * @param collection
     */
    // eslint-disable-next-line no-unused-vars
    onSelect?: (collection: CollectionType) => any;
  }
> = ({ productId, open, onClose, onSelect, ...props }) => {
  const { api, user } = useUser();
  const { i18n } = useLingui();
  const [collections, setCollections] = useState<CollectionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inNewCollecction, setInNewCollection] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!productId) {
      setInNewCollection(false);
    }
  }, [productId]);

  useEffect(() => {
    if (!open || !user) {
      return;
    }
    setIsLoading(true);
    api
      ?.getCurrentUserCollections(productId)
      .then(setCollections)
      .finally(() => {
        return setIsLoading(false);
      });
  }, [api, user, productId, open]);

  const selectCollection = async (collection: CollectionType) => {
    if (onSelect) {
      onSelect(collection);
      return;
    }
    const alreadOnCollection = collection.contentFeed?.some((p) => {
      return p.product.id === productId;
    });
    onClose?.({} as any);

    if (alreadOnCollection) {
      await api.removeProductFromCollection({
        productId: productId!,
        collectionId: collection.id,
      });
      toast.show(
        <div className="flex gap-4 items-center">
          <CollectionLogo color="white" size={40} />
          <span>
            <Trans>
              Removed from collection <b>{collection.name}</b>
            </Trans>
          </span>
        </div>,
      );
      return;
    }

    await api.addProductToCollection({
      productId: productId!,
      collectionId: collection.id,
    });

    toast.show(
      <div className="flex gap-4 items-center">
        <CollectionLogo color="white" size={40} />
        <span>
          <Trans>
            Saved in collection <b>{collection.name}</b>
          </Trans>
        </span>
      </div>,
    );
  };

  return (
    <Modal {...props} open={open} onClose={onClose}>
      <Modal.Card className={styles.card}>
        {inNewCollecction ? (
          <>
            <Icon
              name="chevron-left"
              className="absolute left-5 top-4"
              onClick={() => {
                setInNewCollection(false);
              }}
            />
            <CollectionForm onSave={selectCollection} />
          </>
        ) : (
          <>
            <div>
              <Input placeholder={i18n._(msg`Why did you like it?`)} />
            </div>
            <ul className={styles.collections}>
              <li
                className={styles.collection}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setInNewCollection(true);
                }}
              >
                <CollectionLogo size={75} />
                <Trans>New Collection</Trans>
              </li>
              {collections.map((collection) => {
                return (
                  <li
                    key={collection.id}
                    role="button"
                    tabIndex={0}
                    className={styles.collection}
                    onClick={() => {
                      selectCollection(collection);
                    }}
                  >
                    <CollectionLogo size={75} collection={collection} className={styles.logo} />
                    <span className={styles.collectionDetails}>
                      <span className={styles.collectionTitle}>
                        {collection.isPrivate && <Icon name="lock" />} {collection.name}
                      </span>
                      <span className={styles.collectionContent}>
                        <Plural value={collection.recommendationCount!} one="1 recommendation" other="# recommendations" />
                      </span>
                      {collection.contentFeed?.find((p) => {
                        return p.product.id === productId;
                      }) && (
                        <span className={styles.alreadyInCollection}>
                          <Trans>Already on this collection</Trans>
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
              {isLoading && (
                <li className={styles.collection}>
                  <CollectionLogo size={75} />
                  <Trans>Loading...</Trans>
                </li>
              )}
            </ul>
          </>
        )}
      </Modal.Card>
    </Modal>
  );
};

export default AddToCollection;
